import { supabase } from "@/lib/supabase";
import { resolveGscDateRange } from "@/lib/date-utils";
import { parseRpcOrFallback } from "@/lib/parse-rpc";
import type { UrlActiveFilters } from "@/lib/url-active-filters";
import type { Json } from "@/types/database";
import {
  EMPTY_GSC_DETAIL,
  EMPTY_GSC_DOMAIN_COMPARE,
  EMPTY_GSC_DOMAIN_OVERVIEW,
  EMPTY_GSC_FILTER_OPTIONS,
  EMPTY_GSC_KPI_SUMMARY,
  GSC_MAX_COMPARE_DOMAINS,
  extractGscDetailTotalCount,
  extractGscDomainOverviewTotalCount,
  gscDetailResponseSchema,
  gscDomainCompareResponseSchema,
  gscDomainOverviewResponseSchema,
  gscFilterOptionsSchema,
  gscKpiSummarySchema,
  type GscCompareDomainSummary,
  type GscDateRangeOption,
  type GscDetailResult,
  type GscDetailSortColumn,
  type GscDomainCompareResponse,
  type GscDomainOverviewResult,
  type GscDomainOverviewSortColumn,
  type GscFilterOptions,
  type GscKpiSummary,
  type GscPageSize,
  type GscSortDirection,
  type GscTrendGranularity,
  type GscTrendPoint,
} from "@/types/gsc";

export interface GscDateFilterParams {
  dateRange: GscDateRangeOption;
  customFrom?: string;
  customTo?: string;
  activeFilters: UrlActiveFilters;
}

export interface GscKpiQueryParams extends GscDateFilterParams {}

export interface GscDomainOverviewQueryParams extends GscDateFilterParams {
  page: number;
  pageSize: GscPageSize;
  sortBy: GscDomainOverviewSortColumn;
  sortDir: GscSortDirection;
}

export interface GscTrendQueryParams extends GscDateFilterParams {
  granularity: GscTrendGranularity;
  /** `null` or empty → all domains within scope/filters (RPC `p_domains` omitted). */
  trendDomains: string[] | null;
}

export interface GscDetailQueryParams extends GscDateFilterParams {
  page: number;
  pageSize: GscPageSize;
  sortBy: GscDetailSortColumn;
  sortDir: GscSortDirection;
}

export interface GscCompareQueryParams {
  dateRange: GscDateRangeOption;
  customFrom?: string;
  customTo?: string;
  compareEnabled: boolean;
  domains: string[];
}

class GscService {
  private static instance: GscService;

  private constructor() {}

  public static getInstance(): GscService {
    if (!GscService.instance) {
      GscService.instance = new GscService();
    }
    return GscService.instance;
  }

  private resolveDates(params: {
    dateRange: GscDateRangeOption;
    customFrom?: string;
    customTo?: string;
  }) {
    return resolveGscDateRange(
      params.dateRange,
      params.customFrom,
      params.customTo,
    );
  }

  private toRpcFilters(activeFilters: UrlActiveFilters): Json {
    const backendFilters: Record<string, string | string[]> = {};

    for (const [key, filter] of Object.entries(activeFilters)) {
      if (!filter) continue;

      if ("values" in filter) {
        backendFilters[key] = filter.values;
      } else if ("value" in filter) {
        backendFilters[key] = filter.value;
      }
    }

    return backendFilters as Json;
  }

  public async getFilterOptions(): Promise<GscFilterOptions> {
    const { data, error } = await supabase.rpc("get_gsc_filter_options");

    if (error) {
      console.error("Error loading GSC filter options:", error);
      throw new Error(error.message);
    }

    return parseRpcOrFallback(
      gscFilterOptionsSchema,
      data,
      EMPTY_GSC_FILTER_OPTIONS,
      "get_gsc_filter_options",
    );
  }

  public async getKpiSummary(params: GscKpiQueryParams): Promise<GscKpiSummary> {
    const { dateFrom, dateTo } = this.resolveDates(params);

    const { data, error } = await supabase.rpc("get_gsc_kpi_summary", {
      p_date_from: dateFrom,
      p_date_to: dateTo,
      p_filters: this.toRpcFilters(params.activeFilters),
    });

    if (error) {
      console.error("Error loading GSC KPI summary:", error);
      throw new Error(error.message);
    }

    return parseRpcOrFallback(
      gscKpiSummarySchema,
      data?.[0],
      EMPTY_GSC_KPI_SUMMARY,
      "get_gsc_kpi_summary",
    );
  }

  public async getTrend(params: GscTrendQueryParams): Promise<GscTrendPoint[]> {
    const { dateFrom, dateTo } = this.resolveDates(params);
    const selectedDomains =
      params.trendDomains && params.trendDomains.length > 0
        ? params.trendDomains
        : undefined;

    const { data, error } = await supabase.rpc("get_gsc_trend", {
      p_date_from: dateFrom,
      p_date_to: dateTo,
      p_granularity: params.granularity,
      p_domains: selectedDomains,
      p_filters: this.toRpcFilters(params.activeFilters),
    });

    if (error) {
      console.error("Error loading GSC trend:", error);
      throw new Error(error.message);
    }

    return data ?? [];
  }

  public async getDetail(params: GscDetailQueryParams): Promise<GscDetailResult> {
    const { dateFrom, dateTo } = this.resolveDates(params);

    const { data, error } = await supabase.rpc("get_gsc_detail", {
      p_date_from: dateFrom,
      p_date_to: dateTo,
      p_filters: this.toRpcFilters(params.activeFilters),
      p_sort_by: params.sortBy,
      p_sort_dir: params.sortDir,
      p_page: params.page,
      p_page_size: params.pageSize,
      p_compare_enabled: false,
    });

    if (error) {
      console.error("Error loading GSC detail:", error);
      throw new Error(error.message);
    }

    const rows = parseRpcOrFallback(
      gscDetailResponseSchema,
      data,
      EMPTY_GSC_DETAIL,
      "get_gsc_detail",
    );

    return {
      rows,
      totalCount: extractGscDetailTotalCount(rows),
    };
  }

  public async getDomainOverview(
    params: GscDomainOverviewQueryParams,
  ): Promise<GscDomainOverviewResult> {
    const { dateFrom, dateTo } = this.resolveDates(params);

    const { data, error } = await supabase.rpc("get_gsc_domain_overview", {
      p_date_from: dateFrom,
      p_date_to: dateTo,
      p_filters: this.toRpcFilters(params.activeFilters),
      p_limit: params.pageSize,
      p_offset: (params.page - 1) * params.pageSize,
      p_sort_by: params.sortBy,
      p_sort_dir: params.sortDir,
    });

    if (error) {
      console.error("Error loading GSC domain overview:", error);
      throw new Error(error.message);
    }

    const rows = parseRpcOrFallback(
      gscDomainOverviewResponseSchema,
      data,
      EMPTY_GSC_DOMAIN_OVERVIEW,
      "get_gsc_domain_overview",
    );

    return {
      rows,
      totalCount: extractGscDomainOverviewTotalCount(rows),
    };
  }

  public async getDomainCompare(
    params: GscCompareQueryParams,
  ): Promise<GscDomainCompareResponse> {
    const domains = Array.isArray(params.domains)
      ? params.domains.slice(0, GSC_MAX_COMPARE_DOMAINS)
      : [];

    if (domains.length === 0) {
      return EMPTY_GSC_DOMAIN_COMPARE;
    }

    const { dateFrom, dateTo } = this.resolveDates(params);

    const { data, error } = await supabase.rpc("get_gsc_domain_compare", {
      p_date_from: dateFrom,
      p_date_to: dateTo,
      p_domains: domains,
      p_compare_enabled: params.compareEnabled,
    });

    if (error) {
      console.error("Error loading GSC domain compare:", error);
      throw new Error(error.message);
    }

    return parseRpcOrFallback(
      gscDomainCompareResponseSchema,
      data,
      EMPTY_GSC_DOMAIN_COMPARE,
      "get_gsc_domain_compare",
    );
  }

  /**
   * Flat per-domain GSC metrics for a plain date range, for merging into the
   * Clicks grid by domain. Takes resolved from/to strings (the Clicks page
   * resolves its own DateRangeOption) rather than GSC's param shape. Never
   * throws — GSC coverage is tiny and the RPC may be ungranted; on any problem
   * it returns [] so the grid still renders.
   */
  public async getDomainMetricsForRange(
    dateFrom: string | null,
    dateTo: string,
  ): Promise<
    {
      domain: string;
      clicks: number;
      impressions: number;
      ctr: number;
      position: number;
    }[]
  > {
    const { data, error } = await supabase.rpc("get_gsc_domain_overview", {
      // p_date_from is required; "all time" (null) floors to before any GSC data.
      p_date_from: dateFrom ?? "2020-01-01",
      p_date_to: dateTo,
      p_filters: {},
      p_limit: 2000,
      p_offset: 0,
      p_sort_by: "clicks",
      p_sort_dir: "desc",
    });

    if (error) {
      console.warn("GSC domain metrics unavailable:", error.message);
      return [];
    }

    const rows = (data ?? []) as Array<Record<string, unknown>>;
    return rows.map((r) => ({
      domain: String(r.domain ?? ""),
      clicks: Number(r.clicks ?? 0),
      impressions: Number(r.impressions ?? 0),
      ctr: Number(r.ctr ?? 0),
      position: Number(r.avg_position ?? 0),
    }));
  }

  /**
   * The latest date GSC has data for (Google delivers with a 2-3 day lag), for
   * the freshness badge. Looks back ~35 days and takes the max bucket. Never
   * throws — returns null if unavailable.
   */
  public async getFreshness(): Promise<string | null> {
    const iso = (d: Date) => d.toISOString().slice(0, 10);
    const to = new Date();
    const from = new Date(to.getTime() - 35 * 86_400_000);

    const { data, error } = await supabase.rpc("get_gsc_trend", {
      p_date_from: iso(from),
      p_date_to: iso(to),
      p_granularity: "day",
      p_domains: undefined,
      p_filters: {},
    });

    if (error) {
      console.warn("GSC freshness unavailable:", error.message);
      return null;
    }

    const dates = (data ?? [])
      .map((r) => r.bucket_date)
      .filter((d): d is string => Boolean(d));
    return dates.length ? dates.reduce((a, b) => (a > b ? a : b)) : null;
  }
}

export const gscService = GscService.getInstance();

export type { GscCompareDomainSummary };
